import type { EFileRecord } from '@tabservice/tabfile-client';
import { Pill } from '@/components/ui/pill';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { RecordStatusPill } from './status-pill';
import { fixMetaFor, needsAttention } from './record-fix';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

/**
 * One row per return. The row opens the record rather than expanding in place:
 * the IRS result and the record's own data are read together while it is being
 * corrected, and neither fits inside a table row.
 */
export function RecordsTable({
  records,
  busy,
  onOpen,
}: {
  records: EFileRecord[];
  busy?: boolean;
  onOpen: (recordRef: string) => void;
}) {
  return (
    <TableWrap busy={busy}>
      <Table>
        <THead>
          <tr>
            <TH>Reference</TH>
            <TH>Recipient</TH>
            <TH>Taxpayer ID</TH>
            <TH>Status</TH>
            <TH data-tour="record-fix" className="px-3">
              Fix
            </TH>
            <TH align="right" className="px-3">
              Errors
            </TH>
          </tr>
        </THead>
        <TBody>
          {[...records].sort((a, b) => a.recordRef.localeCompare(b.recordRef, undefined, { numeric: true })).map((record) => {
            const errors = record.errors ?? [];
            const fix = fixMetaFor(record);
            return (
              <TR
                key={record.recordRef}
                interactive
                tabIndex={0}
                onClick={() => onOpen(record.recordRef)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onOpen(record.recordRef);
                }}
              >
                <TD className="font-medium whitespace-nowrap">{record.recordRef}</TD>
                <TD className="max-w-[120px] truncate">{record.recipientName ?? 'Name not on file'}</TD>
                <TD className="whitespace-nowrap text-muted-foreground">
                  {record.recipientTinMasked ?? '***-**-****'}
                </TD>
                <TD>
                  <RecordStatusPill status={record.status} />
                </TD>
                <TD className="px-3">
                  {fix ? (
                    <Pill tone={fix.tone}>{fix.label}</Pill>
                  ) : needsAttention(record) ? (
                    // The row the IRS refused and nobody has answered yet. It had nothing in
                    // this column at all, which read as "no action available" on precisely the
                    // rows that have one. The button opens the same record drawer the row
                    // opens; it is here so the action is visible from the list.
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpen(record.recordRef);
                      }}
                    >
                      Fix
                    </Button>
                  ) : null}
                </TD>
                <TD
                  numeric
                  className={cn('px-3', errors.length > 0 ? 'text-[color:var(--ts-pill-warning-text)]' : 'text-gray-1')}
                >
                  {errors.length}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </TableWrap>
  );
}
